import React, { useEffect, useState, useRef } from "react";
import { ref, onChildAdded, onChildChanged, onChildRemoved, get } from "firebase/database";
import { db } from "./firebase/config";
import { ClipLoader } from "react-spinners";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const ProductImagesManagerScreen = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFanpage, setSelectedFanpage] = useState("all");
  const [fanpages, setFanpages] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // "all", "active", "inactive"
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    const productsRef = ref(db, `productImages/${userId}`);

    const productMap = new Map();
    const fanpagesMap = new Map();
    let isInitialLoad = true;

    // OPTIMIZED: Use child listeners instead of onValue to avoid downloading entire node on each change
    // This reduces bandwidth: only changed product is downloaded, not all products
    const unsubscribeAdded = onChildAdded(productsRef, (snapshot) => {
      const id = snapshot.key;
      const product = { ...snapshot.val(), id };
      productMap.set(id, product);

      // Extract fanpages from this product
      if (product.fanpages && Array.isArray(product.fanpages)) {
        product.fanpages.forEach((page) => {
          if (page && page.id && !fanpagesMap.has(page.id)) {
            fanpagesMap.set(page.id, {
              id: page.id,
              name: page.name,
              access_token: page.access_token,
            });
          }
        });
      }

      if (isInitialLoad) {
        setIsLoading(false);
        isInitialLoad = false;
      }
      updateProductsState();
    });

    const unsubscribeChanged = onChildChanged(productsRef, (snapshot) => {
      const id = snapshot.key;
      const product = { ...snapshot.val(), id };
      productMap.set(id, product);

      // Extract fanpages from this product
      if (product.fanpages && Array.isArray(product.fanpages)) {
        product.fanpages.forEach((page) => {
          if (page && page.id && !fanpagesMap.has(page.id)) {
            fanpagesMap.set(page.id, {
              id: page.id,
              name: page.name,
              access_token: page.access_token,
            });
          }
        });
      }

      updateProductsState();
    });

    const unsubscribeRemoved = onChildRemoved(productsRef, (snapshot) => {
      const id = snapshot.key;
      productMap.delete(id);
      updateProductsState();
    });

    const updateProductsState = () => {
      const productList = Array.from(productMap.values());
      setProducts(productList);
      setFanpages(Array.from(fanpagesMap.values()));
    };

    return () => {
      unsubscribeAdded();
      unsubscribeChanged();
      unsubscribeRemoved();
    };
  }, []);

  const handleFanpageFilter = (fanpageId) => {
    setSelectedFanpage(fanpageId);
    setIsDropdownOpen(false);
  };

  const handleProductClick = (product) => {
    navigate("/add-photo-list", {
      state: { product },
    });
  };

  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.fanpages?.some((page) =>
        page.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );

    const matchesStatus =
      filterStatus === "all"
        ? true
        : filterStatus === "active"
        ? product.autoPostEnabled
        : !product.autoPostEnabled;

    const matchesFanpage =
      selectedFanpage === "all"
        ? true
        : product.fanpages?.some((page) => page.id === selectedFanpage);

    return matchesSearch && matchesStatus && matchesFanpage;
  });

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setIsStatusDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-4 md:px-8 md:py-8">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6 md:mb-8 text-center">
          Quản Lý Ảnh Sản Phẩm
        </h1>

        {/* Search and Filter Controls */}
        <div className="mb-6 space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 md:flex md:flex-row md:gap-4">
          <div className="sm:col-span-2 md:flex-1">
            <input
              type="text"
              placeholder="Tìm kiếm theo tên sản phẩm hoặc tên fanpage..."
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status Dropdown */}
          <div className="md:w-40 relative" ref={statusDropdownRef}>
            <button
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              className={`w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center ${
                filterStatus !== "all" ? "bg-white" : "bg-gray-50"
              }`}
            >
              <span>
                {filterStatus === "all"
                  ? "Trạng thái"
                  : filterStatus === "active"
                  ? "Đang chạy"
                  : "Đã tắt"}
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${
                  isStatusDropdownOpen ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            {isStatusDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-2">
                <button
                  onClick={() => {
                    setFilterStatus("all");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Trạng thái
                </button>
                <button
                  onClick={() => {
                    setFilterStatus("active");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Đang chạy
                </button>
                <button
                  onClick={() => {
                    setFilterStatus("inactive");
                    setIsStatusDropdownOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Đã tắt
                </button>
              </div>
            )}
          </div>

          {/* Fanpage Dropdown - Di chuyển vào layout mới */}
          <div className="md:w-40 relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center ${
                selectedFanpage !== "all" ? "bg-white" : "bg-gray-50"
              }`}
            >
              <span className="truncate">
                {selectedFanpage === "all"
                  ? "Chọn Fanpage"
                  : fanpages.find((page) => page.id === selectedFanpage)?.name}
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${
                  isDropdownOpen ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </button>
            {isDropdownOpen && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-2">
                <button
                  onClick={() => handleFanpageFilter("all")}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100"
                >
                  Chọn Fanpage
                </button>
                {fanpages.map((page) => (
                  <button
                    key={page.id}
                    onClick={() => handleFanpageFilter(page.id)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 truncate"
                    title={page.name}
                  >
                    {page.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {isLoading ? (
          <div className="flex justify-center items-center">
            <ClipLoader size={50} color={"#123abc"} />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            {products.length === 0
              ? "Không có sản phẩm nào được tạo."
              : "Không tìm thấy kết quả phù hợp với điều kiện tìm kiếm"}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg transition-all duration-300 ease-in-out transform hover:-translate-y-1 hover:scale-[1.02]"
                onClick={() => handleProductClick(product)}
              >
                <h2 className="text-2xl font-bold text-gray-800 mb-2 truncate">
                  {product.name}
                </h2>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {Object.values(product.images || {})
                    .slice(0, 2)
                    .map((image, imgIndex) => (
                      <div key={image.id} className="relative">
                        <img
                          src={image.url}
                          alt={`Product ${product.name} Image ${imgIndex}`}
                          className="w-full h-40 object-contain bg-gray-100"
                        />
                        {image.postStatus && (
                          <div className="absolute bottom-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs">
                            Đã đăng
                          </div>
                        )}
                      </div>
                    ))}
                </div>
                {product.fanpages && product.fanpages.length > 0 && (
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-2">
                      {product.fanpages.map((page, pageIndex) => (
                        <span
                          key={pageIndex}
                          className="bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded"
                        >
                          {page.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-4 flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    {Object.values(product.images || {}).length} ảnh
                    {" • "}
                    {
                      Object.values(product.images || {}).filter(
                        (img) => img.postStatus
                      ).length
                    }{" "}
                    đã đăng
                  </div>
                  <div className="flex items-center gap-2">
                    {product.autoPostEnabled && product.postTime && (
                      <span className="text-blue-700 bg-blue-100 rounded px-2 py-1">
                        {product.postTime}
                      </span>
                    )}
                    <div
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        product.autoPostEnabled
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {product.autoPostEnabled ? "🔵 Đang chạy" : "⚪ Đã tắt"}
                    </div>
                  </div>
                </div>
                {Object.values(product.images || {}).length > 0 &&
                  Object.values(product.images || {}).every(
                    (img) => img.postStatus
                  ) && (
                    <div className="absolute top-2 right-2 bg-orange-500 text-white px-2 py-1 rounded-full text-xs">
                      Đã đăng hết ảnh
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}
      </div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={true}
        pauseOnHover={true}
        draggable={true}
      />
    </div>
  );
};

export default ProductImagesManagerScreen;
